import type { BackgroundVariant, BorderVariant, TextVariant, ThemeColours } from "@qendo/theme";
import base from "./base";

const text: Record<TextVariant, string> = {
    primary: base.slate["100"],
    secondary: base.slate["400"],
    tertiary: base.slate["500"],
    quaternary: base.slate["500"],
    info: base.navy["300"],
    success: base.green["400"],
    warning: base.yellow["400"],
    danger: base.red["400"],
    selected: base.pink["500"],
    brand: base.pink["500"],
    neutral: base.slate["400"],
    "inverse-primary": base.slate["900"],
    "inverse-secondary": base.slate["700"],
    link: base.pink["400"],
    "link-pressed": base.pink["500"],
    "link-disabled": base.slate["600"],
};
const icon = text;

const border: Record<BorderVariant, string> = {
    primary: base.slate["700"],
    secondary: base.slate["800"],
    tertiary: base.slate["600"],
    quaternary: base.slate["500"],
    info: base.navy["300"],
    success: base.green["700"],
    warning: base.yellow["700"],
    danger: base.red["400"],
    selected: base.pink["500"],
    brand: base.pink["900"],
    neutral: base.slate["800"],
};

const background: Record<BackgroundVariant, string> = {
    info: base.navy["800"],
    success: base.green["900"],
    warning: base.yellow["900"],
    danger: base.red["900"],
    brand: base.pink["950"],
    primary: base.slate["800"],
    "primary-pressed": base.slate["800"],
    secondary: base.slate["900"],
    "secondary-pressed": base.slate["700"],
    tertiary: base.slate["700"],
    transparent: base.transparent,
    "gradient-start": "#6F253FCC",
    "gradient-end": "#6F253F00",
};

export const theme: ThemeColours = {
    brand: border.brand,
    brandHighlight: text.brand,
    boxShadow: "#0d0d0d14",
    backdrop: "rgba(20, 30, 102, 0.5)", // base.blue["900"]/50% opacity
    text,
    icon,
    border,
    background,
    button: {
        primary: text["inverse-primary"],
        "primary-pressed": text["inverse-secondary"],
        "primary-disabled": text.tertiary,
        "primary-background": base.pink["500"],
        "primary-pressed-background": base.pink["600"],
        "primary-disabled-background": base.slate["800"],
        secondary: base.pink["200"],
        "secondary-pressed": base.pink["300"],
        "secondary-disabled": text.tertiary,
        "secondary-background": base.pink["800"],
        "secondary-pressed-background": base.pink["900"],
        "secondary-disabled-background": base.slate["800"],
        quiet: base.pink["500"],
        "quiet-pressed": base.pink["600"],
        "quiet-disabled": text.tertiary,
        "quiet-background": base.pink["0"],
        "quiet-pressed-background": base.transparent,
        "quiet-disabled-background": base.transparent,
        "quiet-inverse": text["inverse-primary"],
        "quiet-inverse-pressed": text["inverse-primary"],
        "quiet-inverse-disabled": text.tertiary,
        "quiet-inverse-background": base.transparent,
        "quiet-inverse-pressed-background": base.transparent,
        "quiet-inverse-disabled-background": base.transparent,
        neutral: base.pink["500"],
        "neutral-pressed": base.pink["600"],
        "neutral-disabled": text.tertiary,
        "neutral-background": base.slate["800"],
        "neutral-pressed-background": base.slate["700"],
        "neutral-disabled-background": base.slate["900"],
        "neutral-quiet": base.pink["500"],
        "neutral-quiet-pressed": base.pink["600"],
        "neutral-quiet-disabled": text.tertiary,
        "neutral-quiet-background": base.transparent,
        "neutral-quiet-pressed-background": base.transparent,
        "neutral-quiet-disabled-background": base.transparent,
        destructive: base.red["300"],
        "destructive-pressed": base.red["400"],
        "destructive-disabled": text.tertiary,
        "destructive-background": base.red["900"],
        "destructive-pressed-background": base.red["700"],
        "destructive-disabled-background": base.slate["800"],
        "destructive-quiet": base.red["300"],
        "destructive-quiet-pressed": base.red["400"],
        "destructive-quiet-disabled": text.tertiary,
        "destructive-quiet-background": base.transparent,
        "destructive-quiet-pressed-background": base.transparent,
        "destructive-quiet-disabled-background": base.transparent,
        warning: text.warning,
        "warning-pressed": text.warning,
        "warning-disabled": text.tertiary,
        "warning-background": base.yellow["900"],
        "warning-pressed-background": base.yellow["700"],
        "warning-disabled-background": base.slate["700"],
        "warning-quiet": text.warning,
        "warning-quiet-pressed": text.warning,
        "warning-quiet-disabled": text.tertiary,
        "warning-quiet-background": base.transparent,
        "warning-quiet-pressed-background": base.transparent,
        "warning-quiet-disabled-background": base.transparent,
        success: text.success,
        "success-pressed": text.success,
        "success-disabled": text.tertiary,
        "success-background": base.green["900"],
        "success-pressed-background": base.green["700"],
        "success-disabled-background": base.slate["700"],
        "success-quiet": text.success,
        "success-quiet-pressed": text.success,
        "success-quiet-disabled": text.tertiary,
        "success-quiet-background": base.transparent,
        "success-quiet-pressed-background": base.transparent,
        "success-quiet-disabled-background": base.transparent,
    },
    toggle: {
        indicator: base.slate["900"],
        disabled: base.slate["200"],
        off: base.slate["600"],
        on: base.pink["500"],
    },
    check: {
        selector: base.pink["500"],
        background: base.pink["900"],
        disabled: text.tertiary,
    },
    toast: {
        background: base.navy["800"],
        foreground: base.navy["100"],
    },
    tracking: {
        indicator: base.pink["500"],
        selected: base.pink["500"],
        "past-period": base.pink["800"],
        info: base.navy["300"],
        current: base.pink["600"],
        prediction: base.slate["600"],
        feature: base.pink["600"],
        unselected: base.pink["900"],
        "gradient-top": base.pink["600"],
        "gradient-bottom": base.pink["900"],
    },
    input: {
        background: background.primary,
        border: border.primary,
        text: text.primary,
        placeholder: text.tertiary,
        icon: icon.secondary,
        "background-disabled": background.secondary,
        "text-disabled": text.secondary,
        "icon-disabled": icon.tertiary,
        "border-disabled": border.secondary,
        "border-focus": border.selected,
        "border-error": border.danger,
    },
    tabs: {
        background: base.pink["950"],
        selected: base.pink["400"],
    },
    reports: [
        text.brand,
        base.blue["300"],
        base.green["400"],
        base.yellow["400"],
        base.red["300"],
        base.navy["200"],
        base.slate["500"],
        base.pink["200"],
        base.green["200"],
        base.yellow["200"],
    ],
};

export default theme;
